// redux/slices/allpaySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

const clearTokenOnAuthError = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch {}
  }
};

const pickConnected = (payload) => {
  const raw =
    payload?.connected ??
    payload?.data?.connected ??
    payload?.status?.connected ??
    payload?.result?.connected;

  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw === 1;

  if (typeof raw === "string") {
    const value = raw.trim().toLowerCase();
    return value === "true" || value === "1";
  }

  return false;
};

const pickMaskedLogin = (payload) =>
  payload?.maskedLogin ??
  payload?.data?.maskedLogin ??
  payload?.status?.maskedLogin ??
  null;

const pickUpdatedAt = (payload) =>
  payload?.updatedAt ??
  payload?.data?.updatedAt ??
  payload?.status?.updatedAt ??
  null;

const hasExplicitConnected = (payload) => {
  const value =
    payload?.connected ??
    payload?.data?.connected ??
    payload?.status?.connected ??
    payload?.result?.connected;

  return (
    typeof value === "boolean" ||
    typeof value === "number" ||
    typeof value === "string"
  );
};

/* ========================= THUNKS ========================= */

export const saveAllPayCreds = createAsyncThunk(
  "allpay/saveCreds",
  async ({ uid, apiLogin, apiKey }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      await apiRequest("POST", "/allpay/credentials", {
        uid,
        apiLogin,
        apiKey,
      });

      const response = await apiRequest(
        "GET",
        `/allpay/credentials/${encodeURIComponent(uid)}`
      );

      return response?.data || response;
    } catch (error) {
      await clearTokenOnAuthError(error);
      return rejectWithValue(getError(error, "Save creds failed"));
    }
  }
);

export const fetchAllPayStatus = createAsyncThunk(
  "allpay/fetchStatus",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "GET",
        `/allpay/credentials/${encodeURIComponent(uid)}`
      );

      return response?.data || response;
    } catch (error) {
      await clearTokenOnAuthError(error);
      return rejectWithValue(getError(error, "Status failed"));
    }
  }
);

export const createAllPayPayment = createAsyncThunk(
  "allpay/createPayment",
  async ({ uid, payload }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest("POST", "/allpay/payment-intent", {
        uid,
        payload,
      });

      return response?.data || response;
    } catch (error) {
      await clearTokenOnAuthError(error);
      return rejectWithValue(getError(error, "Create payment failed"));
    }
  }
);

export const createAllPayLink = createAsyncThunk(
  "allpay/createLink",
  async ({ uid, order }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest("POST", "/allpay/payment-link", {
        uid,
        order,
      });

      return response?.data || response;
    } catch (error) {
      await clearTokenOnAuthError(error);
      return rejectWithValue(getError(error, "Create link failed"));
    }
  }
);

export const generateAllPayLinkToken = createAsyncThunk(
  "allpay/generateLinkToken",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await apiRequest(
        "POST",
        "/allpay/generate-link-token",
        { uid }
      );

      return response?.data || response;
    } catch (error) {
      await clearTokenOnAuthError(error);
      return rejectWithValue(getError(error, "Generate link token failed"));
    }
  }
);

/* ========================= STATE ========================= */

const initialState = {
  connected: false,
  maskedLogin: null,
  updatedAt: null,

  saving: false,
  status: "idle",
  error: null,

  lastPaymentUrl: null,
  creatingPayment: false,

  lastLinkUrl: null,
  creatingLink: false,

  linkToken: null,
  linkTokenExpiry: null,
  generatingToken: false,
};

/* ========================= SLICE ========================= */

const allpaySlice = createSlice({
  name: "allpay",
  initialState,
  reducers: {
    clearAllPayError: (state) => {
      state.error = null;
    },
    clearPaymentUrl: (state) => {
      state.lastPaymentUrl = null;
    },
    clearLinkUrl: (state) => {
      state.lastLinkUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveAllPayCreds.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveAllPayCreds.fulfilled, (state, action) => {
        state.saving = false;
        state.connected = pickConnected(action.payload);
        state.maskedLogin = pickMaskedLogin(action.payload);
        state.updatedAt = pickUpdatedAt(action.payload);
      })
      .addCase(saveAllPayCreds.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.payload || action.error?.message || "Save creds failed";
      })

      .addCase(fetchAllPayStatus.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllPayStatus.fulfilled, (state, action) => {
        state.status = "succeeded";

        if (hasExplicitConnected(action.payload)) {
          state.connected = pickConnected(action.payload);
          state.maskedLogin = pickMaskedLogin(action.payload);
          state.updatedAt = pickUpdatedAt(action.payload);
        }
      })
      .addCase(fetchAllPayStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "Status failed";
      })

      .addCase(createAllPayPayment.pending, (state) => {
        state.creatingPayment = true;
        state.error = null;
      })
      .addCase(createAllPayPayment.fulfilled, (state, action) => {
        state.creatingPayment = false;
        state.lastPaymentUrl = action.payload?.payment_url || null;
      })
      .addCase(createAllPayPayment.rejected, (state, action) => {
        state.creatingPayment = false;
        state.error =
          action.payload || action.error?.message || "Create payment failed";
      })

      .addCase(createAllPayLink.pending, (state) => {
        state.creatingLink = true;
        state.error = null;
      })
      .addCase(createAllPayLink.fulfilled, (state, action) => {
        state.creatingLink = false;
        state.lastLinkUrl = action.payload?.payment_url || null;
      })
      .addCase(createAllPayLink.rejected, (state, action) => {
        state.creatingLink = false;
        state.error =
          action.payload || action.error?.message || "Create link failed";
      })

      .addCase(generateAllPayLinkToken.pending, (state) => {
        state.generatingToken = true;
        state.error = null;
      })
      .addCase(generateAllPayLinkToken.fulfilled, (state, action) => {
        state.generatingToken = false;
        state.linkToken = action.payload?.connectUrl || null;
        state.linkTokenExpiry = action.payload?.expiresAt || null;
      })
      .addCase(generateAllPayLinkToken.rejected, (state, action) => {
        state.generatingToken = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Generate link token failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

export const { clearAllPayError, clearPaymentUrl, clearLinkUrl } =
  allpaySlice.actions;

export default allpaySlice.reducer;

/* ========================= SELECTORS ========================= */

export const selectAllPay = (state) => state.allpay || initialState;
export const selectAllPayConnected = (state) =>
  Boolean(state.allpay?.connected);
export const selectAllPayStatus = (state) =>
  state.allpay?.status || "idle";
